import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { tProject, getProjectLang, subscribeProjectLang } from '../../lib/project-i18n';
import './MyTaskDuePicker.css';

/**
 * 기한 고르기 팝오버 (2026-09-04) — 신규 파일.
 *
 * 종전 행 액션의 [📅]는 **하루만** 미뤘다(nextDay). 사흘 뒤로 옮기려면 세 번 눌러야 했고,
 * 앞당기거나 기한을 아예 없애는 길은 목록에 없었다 — 상세를 열어야 했다.
 * 여기서는 날짜를 직접 고른다.
 *
 * 왜 `<input type="date">` 인가: 달력 UI 를 손으로 그리면 키보드 조작·로케일·모바일
 * 기본 피커를 전부 다시 만들어야 한다. 이 앱에는 공용 달력 컴포넌트가 없고, 이 자리
 * 하나를 위해 만들 만한 것도 아니다. 브라우저 것이 접근성·입력기까지 이미 맞춰져 있다.
 *
 * 자주 쓰는 값은 버튼으로 따로 둔다 — 날짜 입력은 정확하지만 느리고, 실제로 제일 많이
 * 누르는 것은 '내일'·'다음 주' 같은 상대적인 이동이기 때문이다. [기한 없음]은 해제다
 * (서버 PATCH 가 dueDate:null 을 이미 받는다).
 *
 * 위치: 누른 버튼 옆에 띄운다(anchor). 표 안에서 열리므로 화면 밖으로 넘치지 않게
 * 뷰포트 안쪽으로 당긴다. 표를 스크롤하면 기준이 어긋나므로 그때는 닫는다.
 */

export interface DuePickerTarget {
  id: string;
  title: string;
  dueDate: string | null;
  /** 누른 버튼의 화면 좌표 — 팝오버를 그 옆에 세운다. */
  anchor: DOMRect;
}

/** 로컬 시간 기준 YYYY-MM-DD. toISOString 은 UTC 라 한국 시간대에서 하루가 밀린다. */
export function toYmd(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** 오늘로부터 n일 뒤(음수면 앞). 기준은 늘 오늘이다 — 지금 기한이 언제든 '내일'은 내일이다. */
export function addDaysFromToday(n: number, now: Date = new Date()): string {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  d.setDate(d.getDate() + n);
  return toYmd(d);
}

/**
 * 기한 기준으로 n일 미루기 — 기한이 없거나 깨졌으면 오늘을 기준으로 센다.
 * 종전 nextDay(+1일)와 같은 규칙을 일반화한 것이다.
 */
export function shiftDue(dueDate: string | null, days: number, now: Date = new Date()): string {
  const base = dueDate ? new Date(dueDate) : now;
  if (Number.isNaN(base.getTime())) return addDaysFromToday(days, now);
  const d = new Date(base.getFullYear(), base.getMonth(), base.getDate());
  d.setDate(d.getDate() + days);
  return toYmd(d);
}

interface Props {
  target: DuePickerTarget;
  /** 고른 날짜로 확정. null 이면 기한 해제. */
  onPick: (dueDate: string | null) => void;
  onClose: () => void;
}

const PANEL_W = 248;

export function MyTaskDuePicker({ target, onPick, onClose }: Props) {
  useSyncExternalStore(subscribeProjectLang, getProjectLang, getProjectLang);
  const ref = useRef<HTMLDivElement | null>(null);
  const [value, setValue] = useState(target.dueDate ?? '');
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  /* 버튼 아래 왼쪽 정렬. 오른쪽·아래로 넘치면 뷰포트 안으로 당긴다(표 오른쪽 끝 행에서 잘리던 것). */
  useLayoutEffect(() => {
    const a = target.anchor;
    const h = ref.current?.offsetHeight ?? 210;
    let left = a.left;
    let top = a.bottom + 6;
    if (left + PANEL_W > window.innerWidth - 8) left = window.innerWidth - PANEL_W - 8;
    if (left < 8) left = 8;
    if (top + h > window.innerHeight - 8) top = Math.max(8, a.top - h - 6);
    setPos({ top, left });
  }, [target.anchor]);

  /* 바깥 클릭·ESC 로 닫고, 스크롤하면 기준이 어긋나므로 그때도 닫는다. */
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('scroll', onClose, true);
    window.addEventListener('resize', onClose);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('scroll', onClose, true);
      window.removeEventListener('resize', onClose);
    };
  }, [onClose]);

  /** 자주 쓰는 값 — 앞의 셋은 오늘 기준, [+1주]는 지금 기한 기준으로 민다. */
  const quick: Array<{ label: string; to: string }> = [
    { label: tProject('project.mytask.due_q_today'), to: addDaysFromToday(0) },
    { label: tProject('project.mytask.due_q_tomorrow'), to: addDaysFromToday(1) },
    { label: tProject('project.mytask.due_q_next_week'), to: addDaysFromToday(7) },
    { label: tProject('project.mytask.due_q_plus_week'), to: shiftDue(target.dueDate, 7) },
  ];

  return createPortal(
    <div
      ref={ref}
      className="mtd-pop"
      style={pos ? { top: pos.top, left: pos.left } : { visibility: 'hidden' }}
      role="dialog"
      aria-label={tProject('project.mytask.due_pick_aria', { name: target.title })}
    >
      <div className="mtd-pop__head">
        <b className="mtd-pop__title">{tProject('project.mytask.due_pick')}</b>
        <span className="mtd-pop__task" title={target.title}>{target.title}</span>
      </div>

      <div className="mtd-pop__quick">
        {quick.map((q) => (
          <button
            key={q.label}
            type="button"
            className={`mtd-pop__chip${value === q.to ? ' mtd-pop__chip--on' : ''}`}
            onClick={() => onPick(q.to)}
          >
            {q.label}
          </button>
        ))}
      </div>

      <label className="mtd-pop__field">
        <span>{tProject('project.mytask.due_pick_date')}</span>
        <input
          type="date"
          value={value}
          autoFocus
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); onPick(value || null); }
          }}
        />
      </label>

      <div className="mtd-pop__foot">
        {/* 해제는 되돌리기 어려운 쪽이 아니라 그냥 값 비우기다 — 확인창까지 둘 일은 아니다. */}
        <button type="button" className="mtd-pop__clear" onClick={() => onPick(null)}>
          {tProject('project.mytask.due_clear')}
        </button>
        <button type="button" className="mtd-pop__cancel" onClick={onClose}>
          {tProject('project.btn.close')}
        </button>
        <button
          type="button"
          className="mtd-pop__ok"
          disabled={!value}
          onClick={() => onPick(value || null)}
        >
          {tProject('project.mytask.due_apply')}
        </button>
      </div>
    </div>,
    document.body,
  );
}
